import { Car, Truck, Motorcycle as Bike, Boat as Ship, Tractor, Package as Container } from '@phosphor-icons/react';
import React from 'react';
import carIcon from '@/assets/category-icons-premium/car.png';
import truckIcon from '@/assets/category-icons-premium/truck.png';
import vanIcon from '@/assets/category-icons-premium/van.png';
import motorcycleIcon from '@/assets/category-icons-premium/motorcycle.png';
import boatIcon from '@/assets/category-icons-premium/boat.png';
import constructionIcon from '@/assets/category-icons-premium/construction.png';
import agriculturalIcon from '@/assets/category-icons-premium/agricultural.png';
import { VehicleType } from '@/types/car';
import { useTranslationV2 } from '@/hooks/useTranslationV2';

interface CategoryShowcaseGridProps {
  selectedType?: VehicleType;
  onTypeChange: (type: VehicleType) => void;
  counts?: Record<string, number>;
}

const CATEGORIES: {
  type: VehicleType;
  labelKey: string;
  fallback: string;
  subtitleKey: string;
  subtitleFallback: string;
  icon: React.ElementType;
  image: string;
}[] = [
  { type: 'car', labelKey: 'cat_cars', fallback: 'Mașini', subtitleKey: 'cat_cars_sub', subtitleFallback: 'Berline, SUV, coupé', icon: Car, image: carIcon },
  { type: 'van', labelKey: 'cat_vans', fallback: 'Autoutilitare', subtitleKey: 'cat_vans_sub', subtitleFallback: 'Transport marfă', icon: Truck, image: vanIcon },
  { type: 'motorcycle', labelKey: 'cat_motorcycles', fallback: 'Motociclete', subtitleKey: 'cat_moto_sub', subtitleFallback: 'Sport, touring, scuter', icon: Bike, image: motorcycleIcon },
  { type: 'truck', labelKey: 'cat_trucks', fallback: 'Camioane', subtitleKey: 'cat_trucks_sub', subtitleFallback: 'Cap tractor & remorci', icon: Truck, image: truckIcon },
  { type: 'boat', labelKey: 'cat_boats', fallback: 'Ambarcațiuni', subtitleKey: 'cat_boats_sub', subtitleFallback: 'Bărci, iahturi, jet ski', icon: Ship, image: boatIcon },
  { type: 'construction', labelKey: 'cat_construction', fallback: 'Utilaje', subtitleKey: 'cat_construction_sub', subtitleFallback: 'Excavatoare, încărcătoare', icon: Container, image: constructionIcon },
  { type: 'agricultural', labelKey: 'cat_agricultural', fallback: 'Tractoare', subtitleKey: 'cat_agricultural_sub', subtitleFallback: 'Agricole & utilaje', icon: Tractor, image: agriculturalIcon },
];

export const CategoryShowcaseGrid: React.FC<CategoryShowcaseGridProps> = ({
  selectedType,
  onTypeChange,
  counts,
}) => {
  const { t } = useTranslationV2('filters');

  const tr = (key: string, fallback: string) => {
    const v = t(key);
    return v && v !== key ? v : fallback;
  };

  return (
    <section className="w-full">
      <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6 text-center">
        {tr('shop_by_category', 'Cumpără după categorie')}
      </h2>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {CATEGORIES.map((cat) => {
          const isSelected = selectedType === cat.type;
          const count = counts?.[cat.type] ?? 0;
          const label = tr(cat.labelKey, cat.fallback);
          const subtitle = tr(cat.subtitleKey, cat.subtitleFallback);
          const Icon = cat.icon;

          return (
            <button
              key={cat.type}
              onClick={() => onTypeChange(cat.type)}
              className={`group flex flex-col items-center text-center p-4 md:p-5 rounded bg-card border shadow-xs transition-all duration-200 hover:shadow-card-hover hover:-translate-y-0.5
                ${isSelected
                  ? 'border-primary/40 bg-card ring-1 ring-primary/20 shadow-card-hover'
                  : 'border-border/60 hover:border-primary/40'
                }`}
            >
              <div className={`mb-4 flex h-24 w-24 items-center justify-center rounded border transition-colors duration-200 overflow-hidden ${isSelected ? 'border-primary/30 bg-primary/10 text-primary shadow-xs' : 'border-border/60 bg-muted/20 text-primary group-hover:border-primary/25 group-hover:bg-primary/5'}`}>
                <img
                  src={cat.image}
                  alt={label}
                  loading="lazy"
                  width={1024}
                  height={1024}
                  className="h-20 w-20 object-contain"
                />
              </div>

              <h3 className="font-bold text-base md:text-lg text-foreground leading-tight">
                {label}
              </h3>
              <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
                {subtitle}
              </p>

              {count > 0 && <span className="mt-3 inline-flex items-center text-sm font-semibold text-primary underline-offset-4 group-hover:underline">
                {count === 1 ? 'Vezi 1 anunț' : `${tr('shop', 'Vezi')} ${count} anunțuri`}
              </span>}
            </button>
          );
        })}
      </div>
    </section>
  );
};
